import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';

/** Material UI Packages. */
import { Box, Dialog } from '@mui/material';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { MainDatagrid } from 'shared/ui/Datagrid';
import { callAPI } from 'shared/api/callApi';
import { PaperComponent } from 'shared/ui/DraggableDialog';
import DialogHeader from 'shared/ui/DialogHeader';

import { clientCols, subClientCol } from '../lib/ClientSettingDef';

export default function ClientDialog(props) {
    /** Props from parent component */
    const {
        type = 'client', // 'client' or 'subclient'
        tabValue,
        teamSettingRows,
        setTeamSettingRows,
        clientAssignmentRows,
        setClientAssignmentRows,
        tradePartner,
        dialogOpen,
        setDialogOpen,
        selectedId,
        rowSelectionModel,
        setRowSelectionModel,
        // Subclient specific props
        subClientList,
        setSubClientList,
        subClientRows,
        setSubClientRows,
    } = props;

    /** Auth0 user and token */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Client rows for the datagrid */
    const [clientRows, setClientRows] = useState([]);

    /**
     * Update clientRows based on type, tabValue and selectedId.
     * team_setting: Show the full list of trade partners.
     * client_assignments: Filter and show only the trade partners belonging ot the assigned team.
     */
    useEffect(() => {
        if (!selectedId) return;

        if (type === 'client') {
            if (tabValue === 'team_setting') {
                const selectedItems = teamSettingRows.find((item) => item.id === selectedId)?.il_trade_partner_id;
                setRowSelectionModel(selectedItems || []);
                setClientRows(tradePartner);
            } else {
                const selectedItems = clientAssignmentRows.find((item) => item.id === selectedId)?.il_trade_partner_id;
                setRowSelectionModel(selectedItems || []);

                const assignedTeamIds = clientAssignmentRows.find((user) => user.id === selectedId)?.il_team_id || [];
                const assignedClients = teamSettingRows
                    .filter((team) => assignedTeamIds.includes(team.id))
                    .flatMap((team) => team.il_trade_partner_id);

                const uniqueClients = [...new Set(assignedClients)];
                const filteredClients = tradePartner.filter((client) => uniqueClients.includes(client.id));
                setClientRows(filteredClients);
            }
        }
    }, [selectedId, tabValue, type, dialogOpen]);

    /** Handle to close a dialog. */
    const handleDialogClose = useCallback(() => {
        setDialogOpen(false);
    }, []);

    /**
     * Update rows with new data (for client dialog).
     * @param {Array} rows - Current rows
     * @param {Function} setRows - Setter function
     * @returns {Object} Updated rows and modified row
     */
    const updateRows = useCallback(
        (rows) => {
            const updatedRows = rows.map((item) => {
                if (item.id === selectedId) {
                    return { ...item, il_trade_partner_id: rowSelectionModel };
                }
                return item;
            });

            const modifiedRow = updatedRows.find((item) => item.id === selectedId);
            return { updatedRows, modifiedRow };
        },
        [selectedId, rowSelectionModel],
    );

    /**
     * Extract unique subclient IDs from rows
     * @param {Array} rows - Subclient rows
     * @returns {Array} Unique subclient IDs
     */
    const extractUniqueSubClients = useCallback((rows) => {
        const allSubClients = rows.map((row) => row.il_trade_partner_id_shipper).flat();
        return [...new Set(allSubClients)];
    }, []);

    /**
     * Update user rows with subclient data
     * @param {Array} uniqueSubClients - Unique subclient IDs
     */
    const updateClientAssignmentRows = useCallback(
        (uniqueSubClients) => {
            setClientAssignmentRows((prevRows) =>
                prevRows.map((item) => {
                    if (item.id === subClientRows[0]?.il_user_id) {
                        return { ...item, subclient: uniqueSubClients };
                    }
                    return item;
                }),
            );
        },
        [subClientRows],
    );

    /** Synchronize updated subClientRow with subClientList. */
    const updateSubClientList = useCallback(() => {
        subClientRows.forEach((row) => {
            const existingIndex = subClientList.findIndex(
                (item) =>
                    item.il_trade_partner_id_forwarder.id === row.il_trade_partner_id_forwarder.id &&
                    item.il_user_id === row.il_user_id,
            );

            if (existingIndex > -1) {
                // Overwrite existing value
                subClientList[existingIndex] = row;
            } else {
                // Add new value
                subClientList.push(row);
            }
        });
    }, [subClientRows, subClientList]);

    /** Save data based on dialog type. */
    const handleSave = () => {
        if (type === 'client') {
            // Client dialog save logic
            const rows = tabValue === 'team_setting' ? teamSettingRows : clientAssignmentRows;
            const setRows = tabValue === 'team_setting' ? setTeamSettingRows : setClientAssignmentRows;

            const { updatedRows, modifiedRow } = updateRows(rows);

            callAPI({
                method: 'put',
                module: 'client_setting',
                component: tabValue,
                type: 'client_list',
                getAccessTokenSilently,
                email: user.email,
                obj: JSON.stringify(modifiedRow),
                onSuccess: (resp) => {
                    const newRow = resp.data;
                    if (tabValue === 'team_setting') {
                        setRows(updatedRows);
                    } else {
                        setRows((prev) => prev.map((row) => (row.id === newRow.il_user.id ? newRow.il_user : row)));
                        setSubClientList(newRow.il_subclient_assignment);
                    }
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Successfully updated.',
                            severity: 'success',
                        }),
                    );
                    setRowSelectionModel([]);
                    setDialogOpen(false);
                },
                onError: () => {
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Something went wrong, please try again.',
                            severity: 'error',
                        }),
                    );
                },
            });
        } else {
            // Subclient dialog save logic
            callAPI({
                method: 'put',
                module: 'client_setting',
                component: 'sub_client',
                type: null,
                getAccessTokenSilently,
                email: user.email,
                obj: JSON.stringify(subClientRows),
                onSuccess: (resp) => {
                    const uniqueSubClients = extractUniqueSubClients(subClientRows);
                    updateClientAssignmentRows(uniqueSubClients);
                    updateSubClientList();
                    setDialogOpen(false);
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Successfully updated.',
                            severity: 'success',
                        }),
                    );
                },
                onError: () => {
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Something went wrong, please try again.',
                            severity: 'error',
                        }),
                    );
                },
            });
        }
    };

    /**
     * Process row update in datagrid
     * @param {object} newRow - Updated row data
     * @returns {object} Updated row
     */
    const handleProcessRowUpdate = useCallback(
        (newRow) => {
            if (type === 'subclient') {
                const updatedRows = subClientRows.map((row) => (row.id === newRow.id ? { ...row, ...newRow } : row));
                setSubClientRows(updatedRows);
            }
            return newRow;
        },
        [type, subClientRows],
    );

    /** Get dialog title based on type */
    const getDialogTitle = () => {
        return type === 'client' ? 'CLIENT LIST' : 'SUB CLIENT LIST';
    };

    /** Get columns based on type */
    const getColumns = () => {
        return type === 'client' ? clientCols : subClientCol(tradePartner);
    };

    /** Get rows based on type */
    const getRows = () => {
        return type === 'client' ? clientRows : subClientRows;
    };

    return (
        <Dialog
            maxWidth="md"
            fullWidth={true}
            open={dialogOpen}
            onClose={handleDialogClose}
            PaperProps={{
                sx: {
                    borderRadius: '18px',
                    width: '700px',
                },
            }}
            PaperComponent={PaperComponent}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '480px' }}>
                <DialogHeader cursor title={getDialogTitle()} onClose={handleDialogClose} onSave={handleSave} />
                <MainDatagrid
                    rows={getRows()}
                    cols={getColumns()}
                    checkboxSelection={type === 'client'}
                    id_status={false}
                    rowSelectionModel={type === 'client' ? rowSelectionModel : undefined}
                    setRowSelectionModel={type === 'client' ? setRowSelectionModel : undefined}
                    processRowUpdate={type === 'subclient' ? handleProcessRowUpdate : undefined}
                    getRowHeight={() => 'auto'}
                    rowHeight={type === 'subclient' ? 35 : undefined}
                    showToolbar={'GridToolbar'}
                    sx={{ px: 2, py: 1 }}
                />
            </Box>
        </Dialog>
    );
}
